import React from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from '../../util/reactIntl';

import IconSearch from '../IconSearch/IconSearch';
import NamedLink from '../NamedLink/NamedLink';
import IconBag from '../IconBag/IconBag';
import IconHeart from '../IconHeart/IconHeart';
import HomeIconMobile from '../HomeIconMobile/HomeIconMobile';
import { selectBookmarkCartCount } from '../../ducks/cart.duck';

import css from './GlobalBottomNavMobile.module.css';

// Auth routes where the bottom nav should not appear
const AUTH_ROUTES = [
  '/login',
  '/signup',
  '/confirm',
  '/recover-password',
  '/reset-password',
  '/verify-email',
];

const SEARCH_BROWSE_ROUTES = ['/s', '/costumes'];

const matchesRoutePrefix = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`);

const getPathSegments = pathname => pathname.split('/').filter(Boolean);

export const getMobileBottomNavState = pathname => {
  const pathSegments = getPathSegments(pathname);
  const primarySegment = pathSegments[0];
  const isAuthRoute = AUTH_ROUTES.some(route => matchesRoutePrefix(pathname, route));
  const isSearchBrowseRoute = SEARCH_BROWSE_ROUTES.some(route =>
    matchesRoutePrefix(pathname, route)
  );
  const isCheckoutFlowRoute = primarySegment === 'l' && pathSegments.includes('checkout');

  return {
    isHidden: isAuthRoute || isCheckoutFlowRoute,
    isElevated: false,
    isSearchBrowseRoute,
  };
};

const tabs = [
  {
    key: 'search',
    route: { name: 'SearchPage' },
    icon: <IconSearch />,
    match: path => getMobileBottomNavState(path).isSearchBrowseRoute,
  },
  {
    key: 'favorites',
    route: { name: 'FavoritesPage' },
    icon: <IconHeart />,
    match: path => path.startsWith('/wishlist'),
  },
  {
    key: 'home',
    route: { name: 'LandingPage' },
    icon: <HomeIconMobile />,
    match: path => path === '/' || path === '/#' || path.startsWith('/p/'),
  },
  {
    key: 'cart',
    route: { name: 'CartPage' },
    icon: <IconBag />,
    match: path => path.startsWith('/cart'),
    hasBadge: true,
  },
];

const GlobalBottomNavMobile = () => {
  const location = useLocation();
  const cartCount = useSelector(selectBookmarkCartCount);
  const intl = useIntl();
  const { isHidden, isElevated } = getMobileBottomNavState(location.pathname);

  if (isHidden) return null;

  return (
    <>
      <div className={classNames(css.spacer, { [css.spacerElevated]: isElevated })} />
      <nav
        className={classNames(css.bottomNavbar, { [css.bottomNavbarElevated]: isElevated })}
        aria-label={intl.formatMessage({ id: 'GlobalBottomNavMobile.ariaLabel' })}
      >
        {tabs.map(tab => {
          const isActive = tab.match(location.pathname);
          const showBadge = tab.hasBadge && cartCount > 0;
          const displayCount = cartCount > 99 ? '99+' : cartCount;

          return (
            <NamedLink
              key={tab.key}
              name={tab.route.name}
              params={tab.route.params}
              className={`${css.navLink} ${isActive ? css.active : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {tab.icon}
              {showBadge ? <span className={css.badge}>{displayCount}</span> : null}
            </NamedLink>
          );
        })}
      </nav>
    </>
  );
};

export default GlobalBottomNavMobile;
